import React, { useState, Fragment, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Container,
} from "reactstrap";
import LaddaButton, { ZOOM_OUT, XS, SLIDE_LEFT } from "react-ladda";
import { FormattedDate } from "react-intl";
import { FormattedNumber } from "../../../FormattedNumber/FormattedNumber";
import InfiniteScroll from "react-infinite-scroller";
import { NumberFormatInput } from "../../../NumberFormatInput/NumberFormatInput";
import { Field, Combobox, Switch, Symbol } from "../../../Modal/Order/Field/Field";
import { LoadingMessage } from "../../Controls";
import { Mode, WizzMode } from "../../types";
import { useEntities } from "../../Wizzard";
import { CircularProgress } from "../../..";

import style from "./../../styles.module.scss";

const Contracts = ({ mode, services, assetContracts, wizzToggle, wizzOnChange, assetId, decimalScale }) => {
  const [readonly, setReadonly] = useState(mode == WizzMode.View);
  const [recalculating, setRecalculating] = useState(false);
  const [modalPayments, setModalPayments] = useState({ open: false });
  const [modalContract, setModalContract] = useState({ open: false });
  const [deleting, setDeleting] = useEntities({});
  const [selection, setSelection] = useState({ active: false, checkeds: {} });
  const [grid, setGrid] = useState({
    hasMore: assetContracts ? assetContracts.length > 15 : false,
    contracts: assetContracts ? assetContracts.slice(0, 14) : null,
    loading: false,
  });

  useEffect(() => {
    if (assetContracts) {
      setReadonly(mode == WizzMode.View);
      setGrid({
        hasMore: assetContracts ? assetContracts.length > 15 : false,
        contracts: assetContracts ? assetContracts.slice(0, 14) : null,
        loading: false,
      });
    }
  }, [assetContracts]);

  const loadMore = (page) => {
    setTimeout(
      () => {
        setGrid({
          contracts: assetContracts.slice(0, (page + 1) * 15 - 1),
          hasMore: assetContracts.length > (page + 1) * 15,
          loading: false,
        });
      },
      page ? 500 : 0
    );
  };

  const handleViewContract = (contract) => {
    setModalContract(() => ({
      open: true,
      contract,
    }));
  };

  const toggleModalContract = () => {
    setModalContract(() => ({
      open: false,
    }));
  };

  return (
    <div className={style.payments}>
      {modalContract.open && (
        <ContractModal
          open={modalContract.open}
          contract={modalContract.contract}
          toggle={toggleModalContract}
          decimalScale={decimalScale}
        />
      )}
      <InfiniteScroll
        loadMore={loadMore}
        hasMore={grid.hasMore}
        loader={<CircularProgress sm key="payments-loader" />}
      >
        <Table responsive>
          <thead>
            <tr>
              <th>Maturity date</th>
              <th className="text-right">Rate</th>
              <th className="text-right">Lender</th>
              <th className="text-right">Borrower</th>
              <th className="text-right">Opening leg</th>
              <th className="text-right">Closing leg</th>
              <th className="text-right">Collateral</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {grid.loading ? (
              <EmptyOrLoading loading={true} />
            ) : grid.contracts ? (
              <Fragment>
                {grid.contracts.map((item) => (
                  <tr key={`pay-${item.ContractMaturity}`}>
                    <td>
                      <FormattedDate value={`${item.ContractMaturity}T00:00`} />
                    </td>
                    <td className="text-right">
                      <FormattedNumber value={item.ContractRate} dataTypeDecimalName="currency" />
                    </td>
                    <td className="text-right">{item.LenderName}</td>
                    <td className="text-right">{item.BorrowerName}</td>
                    <td className="text-right">
                      <FormattedNumber value={item.ContractOpeningLeg} dataTypeDecimalName="currency" />
                    </td>
                    <td className="text-right">
                      <FormattedNumber value={item.ContractClosingLeg} dataTypeDecimalName="currency" />
                    </td>
                    <td className="text-right">{item.ContractCollateralAssetTicker}</td>
                    <td className="text-right">
                      <Button
                        color="primary"
                        size="sm"
                        className="mr-2"
                        onClick={() => handleViewContract(item)}
                      >
                        <i className="fa fa-info-circle" aria-hidden="true"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
                {grid.contracts.length == 0 && <EmptyOrLoading />}
              </Fragment>
            ) : null}
          </tbody>
        </Table>
      </InfiniteScroll>
    </div>
  );
};

const EmptyOrLoading = ({ loading }) => (
  <tr>
    <td colSpan="9">
      {loading ? (
        <LoadingMessage text="Loading contracts" />
      ) : (
        <span className="text-center d-block h5 m-3" style={{ textAlign: "center" }}>
          No contracts
        </span>
      )}
    </td>
  </tr>
);

const ContractModal = ({ open, contract, toggle, decimalScale }) => {
  return (
    <Modal isOpen={open} toggle={toggle} className={style.modal} size="lg">
      <ModalHeader toggle={toggle}>View Contract</ModalHeader>
      <ModalBody>
        <Row>
          <Col>
            <Row>
              <Col>Contract Details</Col>
            </Row>
            <div className={style.contractDetailsWrapper}>
              <Row>
                <Col>
                  <Field label="Asset">
                    <Input type="text" disabled value={contract.ContractCollateralAssetTicker} />
                  </Field>
                </Col>
                <Col>
                  <Row>
                    <Col>
                      <Field label="Quantity">
                        <NumberFormatInput
                          type="text"
                          customInput={Input}
                          value={parseFloat(contract.ContractCollateral)}
                          disabled
                          dataTypeDecimalName="quantity"
                        />
                      </Field>
                    </Col>
                    @
                    <Col>
                      <Field label="Price">
                        <NumberFormatInput
                          type="text"
                          customInput={Input}
                          value={parseFloat(contract.ContractCollateralPrice)}
                          disabled
                          decimalScale={decimalScale}
                          dataTypeDecimalName="price"
                        />
                      </Field>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Row>
                    <Col>
                      <Field label="Appraisal">
                        <NumberFormatInput
                          type="text"
                          customInput={Input}
                          value={parseFloat(contract.ContractAppraisal)}
                          disabled
                          dataTypeDecimalName="percentage"
                          suffix="%"
                        />
                      </Field>
                    </Col>
                    <Col>
                      <Field label="Haircut">
                        <NumberFormatInput
                          type="text"
                          customInput={Input}
                          value={parseFloat(contract.ContractMargin)}
                          disabled
                          dataTypeDecimalName="percentage"
                          suffix="%"
                        />
                      </Field>
                    </Col>
                  </Row>
                </Col>
                <Col>
                  <Row>
                    <Col>
                      <Field label="Market Value">
                        <NumberFormatInput
                          type="text"
                          customInput={Input}
                          value={parseFloat(contract.ContractMarketValue)}
                          disabled
                          decimalScale={decimalScale}
                          dataTypeDecimalName="price"
                        />
                      </Field>
                    </Col>

                    <Col>
                      <Field label="Currency">
                        <Input
                          type="text"
                          placeholder="Currency"
                          disabled
                          value={contract.ContractCollateralCurrencyISO}
                          dataTypeDecimalName="currency"
                        />
                      </Field>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Field label="Maturity">
                    <Input type="date" step="1" readOnly value={contract.ContractMaturity} />
                  </Field>
                </Col>
                <Col>
                  <Row>
                    <Col>
                      <Field label="Rate">
                        <NumberFormatInput
                          type="text"
                          customInput={Input}
                          value={parseFloat(contract.ContractRate)}
                          dataTypeDecimalName="percentage"
                          disabled
                          suffix="%"
                        />
                      </Field>
                    </Col>
                    <Col>
                      <div className={style.leg}>
                        <span className={style.opening}>Opening leg</span>{" "}
                        {contract.ContractCollateralCurrencyISO}{" "}
                        {
                          <FormattedNumber
                            value={contract.ContractOpeningLeg}
                            dataTypeDecimalName="currency"
                          />
                        }
                      </div>
                      <div className={style.leg}>
                        <span className={style.closing}>Closing leg</span>{" "}
                        {contract.ContractCollateralCurrencyISO}{" "}
                        {
                          <FormattedNumber
                            value={contract.ContractClosingLeg}
                            dataTypeDecimalName="currency"
                          />
                        }
                      </div>
                      <CouponPayments payments={contract.Payment} />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export const CouponPayments = ({ payments = [] }) => {
  const [open, setOpen] = useState(false);
  const [total, setTotal] = useState(0);

  const toggle = () => {
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    let newTotal = 0;
    payments.map((payment) => (newTotal += +payment.ContractPaymentAdjustedAmmount));
    setTotal(newTotal);
  }, [payments]);

  return (
    <Fragment>
      {payments.length > 0 && (
        <Fragment>
          <span className={style.couponPaymentsLink} onClick={toggle}>
            View coupon payments
          </span>
          {open && (
            <Modal isOpen={open} toggle={toggle}>
              <ModalHeader toggle={toggle}>Coupon payments</ModalHeader>
              <ModalBody>
                <Table className={style.feesTable} responsive>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Name</th>
                      <th>Payment</th>
                      <th>Adjusted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((coupon, idx) => (
                      <tr key={idx}>
                        <td>
                          <FormattedDate value={`${coupon.ContractPaymentDate}T00:00`} />
                        </td>
                        <td>{coupon.ContractPaymentName}</td>
                        <td>
                          <FormattedNumber
                            value={coupon.ContractPaymentAmmount}
                            dataTypeDecimalName="currency"
                          />
                        </td>
                        <td>
                          <FormattedNumber
                            value={coupon.ContractPaymentAdjustedAmmount}
                            dataTypeDecimalName="currency"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ModalBody>
              <ModalFooter>
                <span className={style.paymentsTotal}>
                  {`Adjusted total: `}
                  <FormattedNumber value={total} dataTypeDecimalName="currency" />
                </span>
                <Button color="light" onClick={toggle}>
                  Close
                </Button>
              </ModalFooter>
            </Modal>
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

export default Contracts;
